<template>
  <div>
    <b-row v-if="editedEntity">
      <b-col md="6">
        <letter-avatar :name="editedEntity.name" :image="editedEntity.avatar" variant="secondary" />
        <h3 class="ml-5">
          {{ editedEntity.name }}
          <br />
          <span class="font-size-14 text-muted">
            {{ editedEntity.email }}
          </span>
        </h3>
      </b-col>
      <b-col md="5">
        <letter-avatar :name="selectedOrganization.name" :image="selectedOrganization.logo" variant="secondary" />
        <h5 class="ml-5 text-muted">
          {{ selectedOrganization.name }}
          <br />
          <span class="font-size-14 ">
            <i class="mdi mdi-hexagon-multiple-outline mr-1"></i>
            {{ selectedOrganization.workspace.name }}
          </span>
        </h5>
      </b-col>
    </b-row>

    <hr />

    <!-- <b-form-checkbox switch size="sm" v-model="form.accessable"> {{ $t("organization.can-access") }}</b-form-checkbox> -->

    <h3 class="mt-4">{{ $t("role.plural") }}</h3>

    <div v-if="roles">
      <div
        v-for="item in filteredRoles"
        :key="item._id"
        @click="selectRole(item)"
        :class="selectionMap[item._id] ? 'border-primary bg-soft-primary' : 'border-gray'"
        class="mb-1 p-2 card card-body hand border "
      >
        <b class="text-primary">{{ item.name | translate }}</b>
        <div>
          {{ item.description | translate }}
        </div>

        <h6 class="mt-3">{{ $t("role.permissions") }}</h6>
        <div v-if="hasToSimplify(item)">
          <div class="card card-body bg-light border border-secondary mb-1 p-1 text-muted">
            {{ $t("process.department-process-admin") }}
          </div>
        </div>
        <div
          v-else
          class="card card-body bg-light border border-secondary mb-1 p-1 text-muted"
          v-for="p in item.permissions"
          :key="item._id + p.definition"
          v-b-tooltip.hover
          :title="$options.filters.translate(p.description)"
        >
          {{ p.name | translate }}
        </div>
      </div>
    </div>
    <!-- TKP-99: Sistem Yöneticisi modül verisini görmez (yetki matrisi); ekranda açıkça söylenir -->
    <small v-if="selectedHasSystemAdmin" class="d-block text-muted mt-2" data-test="system-admin-hint">{{ $t("organization.members.system-admin-hint") }}</small>

    <!-- TKP-99: kapsamsız rol atanamaz -->
    <member-role-scope :roles="selections" v-model="scope" />

    <!-- TKP-99: üyenin elindeki ATANAMAZ roller — listede seçenek değiller. TKP-243 r.3 (karar 2026-11-03): kaydedince kaldırılır
         (eskiden korunuyordu; "Birim / Süreç Yöneticisi" seçilen üye eski Hukuk/BT Yöneticisi yetkileriyle kalıyordu) — önceden uyarılır -->
    <div v-if="retiredRoles.length" class="mt-3 alert alert-warning" data-test="retired-roles">
      <label class="font-weight-bold d-block">{{ $t("organization.members.retired-roles.title") }}</label>
      <small class="d-block mb-2" data-test="retired-roles-hint">{{ $t("organization.members.retired-roles.hint") }}</small>
      <div v-for="role in retiredRoles" :key="'retired-' + role._id" class="mb-1" data-test="retired-role">
        <span style="text-decoration: line-through;">{{ role.name | translate }}</span>
      </div>
    </div>

    <empty v-if="!filteredRoles || filteredRoles.length == 0" width="120px">
      <h6 class="text-center">
        {{ $t("organization.members.empty-accessible-roles") }}
      </h6>
    </empty>

    <sweet-button
      v-if="filteredRoles && filteredRoles.length"
      @click="save()"
      type="submit"
      :loading="isUpdating"
      variant="success"
      icon="mdi mdi-check"
      size="sm"
      buttonClass="mt-2"
    >
      {{ $t("common.save") }}
    </sweet-button>
  </div>
</template>

<script>
import MemberRoleScope from "./member-role-scope";
import { buildMemberUpdatePayload, hasSystemAdminRole, heldRetiredRoles } from "./member-roles";

export default {
  components: { MemberRoleScope },

  data() {
    return {
      form: {},
      removeRoles: [],
      scope: { scopeDepartments: [], scopeProcesses: [] },
      selections: [],
      selectionMap: {},
    };
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.resetForm();
        if (v) {
          this.form = { ...v };

          if (v.roles) {
            v.roles.forEach((i) => this.selectRole(i));
          }
        }
      },
    },
  },
  computed: {
    editedEntity() {
      return this.$store.getters["organizationMember/editedEntity"];
    },
    selectedOrganization() {
      return this.$store.getters["organization/selected"];
    },
    roles() {
      return this.$store.getters[`role/pagination`];
    },
    filteredRoles() {
      const ac = this.accessableRoles.map((i) => i._id);

      return this.roles?.docs?.filter((role) => ac.includes(role._id)) || [];
    },
    accessableRoles() {
      return this.$store.getters["user/accessableRoles"] || [];
    },
    isUpdating() {
      return this.$store.getters["organizationMember/isUpdating"];
    },
    retiredRoles() {
      return heldRetiredRoles(this.editedEntity && this.editedEntity.roles);
    },
    selectedHasSystemAdmin() {
      return hasSystemAdminRole(this.selections);
    },
  },
  mounted() {
    this.$store.dispatch(`role/paginate`, {
      options: { page: 1, limit: 500, sort: { name: "asc" } },
    });
  },
  methods: {
    hasToSimplify(role) {
      // Bu kesinlikle bu şekilde yapılammalıydı. Eğer tek bir permission gibi kullanılmak isteniyorsa, backend'de bu görüntüleme yetkileri tek bir permission olarak tanımlanmalıydı.
      //Görüntüleme yetkisi hem atomik olarak rollere eklemeye devam etmek istiyorlar hem de ekranda her bir permission ayrı ayrı görünmesin istiyorlar. Talep kesinleşene kadar bu şekilde geçici bir çözüm uygulandı
      if (!role) return false;

      const permissions = ["ORGANIZATION_GDPR_ACCESS", "ORGANIZATION_KVKK_ACCESS", "ORGANIZATION_DDO_ACCESS", "ORGANIZATION_BGYS_ACCESS", "ORGANIZATION_RISK_ACCESS"];
      return role.permissions.every((p) => permissions.includes(p.definition));
    },
    selectRole(item) {
      if (this.selectionMap[item._id]) {
        delete this.selectionMap[item._id];
        this.selections = [...this.selections.filter((i) => i._id != item._id)];
      } else {
        this.selections = [...this.selections, item];
        this.selectionMap[item._id] = item;
      }
      this.$forceUpdate();
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationMember/save`, buildMemberUpdatePayload(this.form, this.selections, { removeRoles: this.retiredRoles, ...this.scope }) /* TKP-243 r.3: eski roller kayıtta kalkar */);
        this.resetForm();
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-member-form");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    isMarkedForRemoval(role) {
      return this.removeRoles.some((item) => item._id == role._id);
    },
    toggleRemoval(role) {
      this.removeRoles = this.isMarkedForRemoval(role) ? this.removeRoles.filter((item) => item._id != role._id) : [...this.removeRoles, role];
    },
    resetForm() {
      this.form = {};
      this.selections = [];
      this.selectionMap = {};
      this.removeRoles = [];
      this.scope = { scopeDepartments: [], scopeProcesses: [] };
    },
  },
};
</script>
